import React from "react"
import ReactDOM from "react-dom"
import { Route, Switch, useLocation } from "react-router-dom"

import getSDK from "@ua/web-sdk"
import { ConflictEvent } from "@ua/web-sdk/lib/contact/types"

import { Container, createStyles, makeStyles, Theme } from "@material-ui/core"

import Home from "./pages/home"
import Settings from "./pages/settings"
import NotFound from "./pages/not-found"
import { metadataActions, useAppDispatch } from "./store"
import { loadChannel, trackScreen } from "./store/thunks"
import Preferences from "./pages/preferences"
import Debug from "./pages/debug"
import Navbar from "./Navbar"
import MenuDrawer from "./MenuDrawer"

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    content: {
      maxWidth: 1024,
    },
  })
)

type MapCustomViewProps = {
  place?: string
}

const MapCustomView: React.FC<MapCustomViewProps> = ({ place }) => {
  return (
    <iframe
      width="100%"
      height="100%"
      frameBorder="0"
      style={{ border: 0 }}
      referrerPolicy="no-referrer-when-downgrade"
      src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyDa_rm8V8JWVa4oOERgJT0afBtaE_VAHqQ&q=${
        place ? encodeURIComponent(place) : ""
      }`}
      allowFullScreen
    ></iframe>
  )
}

type PropertiesCustomViewProps = {
  properties: Record<string, string>
}

const PropertiesCustomView: React.FC<PropertiesCustomViewProps> = ({
  properties,
}) => {
  return <pre>{JSON.stringify(properties, undefined, 2)}</pre>
}

const onCustomViewShown = (
  name: string,
  el: HTMLElement,
  properties: Record<string, string>
) => {
  switch (name) {
    case "map_view":
      const { place } = properties
      ReactDOM.render(<MapCustomView place={place} />, el)
      break
    case "properties_view":
      ReactDOM.render(<PropertiesCustomView properties={properties} />, el)
      break
  }
  return {
    destroy: () => ReactDOM.unmountComponentAtNode(el),
  }
}

const App: React.FC = () => {
  const dispatch = useAppDispatch()
  const classes = useStyles()
  const [menuOpen, setMenuOpen] = React.useState(false)

  const location = useLocation()
  React.useEffect(() => {
    dispatch(trackScreen(location.pathname))
  }, [dispatch, location])

  // get and cache the SDK promise
  const UA = React.useMemo(() => getSDK(), [])

  // load channel on app init
  React.useEffect(() => {
    dispatch(loadChannel())
  }, [dispatch])

  const addConflict = React.useCallback(
    (event: ConflictEvent) =>
      dispatch(
        metadataActions.addConflictEvent({ occurred: Date.now(), event })
      ),
    [dispatch]
  )

  // set up the conflict event listener; this isn't the greatest way to handle
  // this, but it is reasonably expedient
  React.useEffect(() => {
    UA.then((sdk) => sdk.contact).then((contact) =>
      contact?.addEventListener("conflict", listener)
    )

    return () => {
      UA.then((sdk) => sdk.contact).then((contact) =>
        contact?.removeEventListener("conflict", listener)
      )
    }

    function listener(ev: CustomEvent<ConflictEvent>) {
      addConflict(ev.detail)
    }
  }, [])

  React.useEffect(() => {
    UA.then(async (sdk) => {
      await sdk.components.inAppAutomation.setCustomViewDelegate({
        onCustomViewShown,
      })
      await sdk.components.inAppAutomation.setPaused(false)
    })
  }, [])

  return (
    <>
      <Navbar setMenuOpen={setMenuOpen} />
      <MenuDrawer open={menuOpen} setOpen={setMenuOpen} />
      <Container maxWidth="md" className={classes.content}>
        <Switch>
          <Route exact path="/">
            <Home />
          </Route>

          <Route exact path="/settings">
            <Settings />
          </Route>

          <Route
            path="/preferences/:id"
            render={(props) => <Preferences {...props.match.params} />}
          />

          <Route exact path="/debug">
            <Debug />
          </Route>

          <Route>
            <NotFound />
          </Route>
        </Switch>
      </Container>
    </>
  )
}

export default App
